import OverlayAniversario from './OverlayAniversario';
import MomentoEspecialYoutubeAudio from './MomentoEspecialYoutubeAudio';
import { TvNetworkHud } from './TvNetworkHud';
import TvEngajamentoHud from './TvEngajamentoHud';
import TvFilaProximaTicker from './TvFilaProximaTicker';
import QrSpotlightVignette from './QrSpotlightVignette';
import {
  FutebolPlacar,
  OutrosEventosOverlay,
  TvIntervaloOverlay,
  TvCopaCountdown,
  TvBolaoTicker,
  TvBolaoResultadoPopup,
} from '@/features/futebol';
import { TvPreGameVoting } from '@/features/futebol-votacao';
import { TvVibeTakeover, TvInvasaoJuninaTakeover } from '@/features/jukebox';
import { TvDiaNamoradosTakeover, TvFestaJuninaTakeover, TvCopaTakeover } from '@/features/temas-sazonais';
import { TvPolaroidOverlay } from '@/features/polaroid';
import type { PlayerConfig } from '../hooks/usePlayerConfig';
import type { UseMomentoEspecialReturn } from '../hooks/useMomentoEspecial';
import type { UseIdentidadeSonoraReturn } from '../hooks/useIdentidadeSonora';
import type { TvEngajamentoState } from '../../domain/entities/Engajamento';
import type {
  FilaJukebox,
  FonteReproducao,
  FutebolPartidaAtiva,
  FutebolJogoDia,
} from '@/shared/types';

export interface PlayerOverlaysProps {
  telaId: string | undefined;
  playerConfig: PlayerConfig;
  origemMidia: FonteReproducao;
  ytUnlocked: boolean;
  momento: Pick<
    UseMomentoEspecialReturn,
    | 'momentoEspecial'
    | 'celebracaoAtiva'
    | 'duracaoCelebracaoSeg'
    | 'handleEnded'
    | 'onFaseChange'
    | 'youtubeVideoId'
    | 'inicioSegEfetivo'
    | 'fimEfetivoAudioSeg'
    | 'birthdayAudioRef'
    | 'audioSrc'
  >;
  identidadeSonora: Pick<
    UseIdentidadeSonoraReturn,
    'locucaoSrc' | 'locucaoAudioRef' | 'handleLocucaoEnded' | 'handleLocucaoError'
  >;
  partidaAtiva: FutebolPartidaAtiva | null;
  partidaJogo: FutebolJogoDia | null;
  lastFutebolEventKey: string | null;
  lastIntervalKey: string | null;
  onFutebolEventoProcessado: (key: string) => void;
  onIntervalProcessado: (key: string) => void;
  modoPlaylistJukebox: boolean;
  engajamentoHud: TvEngajamentoState;
  pedidoAtual: FilaJukebox | null;
  showPedidoPopup: boolean;
  mostrarFilaProxima: boolean;
  raioXVisivel: boolean;
  filaEspera: FilaJukebox[];
  isInvasaoAtiva: boolean;
  hypeQrDestaque: boolean;
  showNetworkHud: boolean;
  isOnline: boolean;
}

/**
 * Overlays, polaroid, HUD, fila, QR spotlight, network HUD e os dois `<audio>`.
 * YoutubeSlide permanece em PlayerContent (mesma posição no VDOM).
 */
export default function PlayerOverlays({
  telaId,
  playerConfig,
  origemMidia,
  ytUnlocked,
  momento,
  identidadeSonora,
  partidaAtiva,
  partidaJogo,
  lastFutebolEventKey,
  lastIntervalKey,
  onFutebolEventoProcessado,
  onIntervalProcessado,
  modoPlaylistJukebox,
  engajamentoHud,
  pedidoAtual,
  showPedidoPopup,
  mostrarFilaProxima,
  raioXVisivel,
  filaEspera,
  isInvasaoAtiva,
  hypeQrDestaque,
  showNetworkHud,
  isOnline,
}: PlayerOverlaysProps) {
  return (
    <>
      {momento.momentoEspecial && (
        <OverlayAniversario
          nome={momento.momentoEspecial.nome}
          duracaoCelebracaoSeg={momento.duracaoCelebracaoSeg}
          onEnded={momento.handleEnded}
          onFaseChange={momento.onFaseChange}
        />
      )}
      {momento.momentoEspecial && momento.youtubeVideoId && momento.celebracaoAtiva && (
        <MomentoEspecialYoutubeAudio
          videoId={momento.youtubeVideoId}
          inicioSeg={momento.inicioSegEfetivo}
          fimSeg={momento.fimEfetivoAudioSeg}
          unlocked={ytUnlocked}
        />
      )}
      {playerConfig.exibir_futebol && (
        <FutebolPlacar
          isModoCaptura={origemMidia !== 'playlist'}
          partida={partidaAtiva}
          jogo={partidaJogo}
          initialEventKey={lastFutebolEventKey}
          onEventoProcessado={onFutebolEventoProcessado}
          temaCopa={playerConfig.config_tema_copa}
          exibirLivePitch={playerConfig.exibir_live_pitch === true}
        />
      )}
      {playerConfig.exibir_futebol && <OutrosEventosOverlay />}
      {playerConfig.exibir_futebol && (
        <TvIntervaloOverlay
          telaId={telaId ?? ''}
          fixtureId={partidaAtiva?.fixture_id ?? null}
          ultimoEvento={partidaAtiva?.ultimo_evento ?? null}
          initialIntervalKey={lastIntervalKey}
          onIntervalProcessado={onIntervalProcessado}
        />
      )}
      {playerConfig.exibir_futebol && <TvCopaCountdown fixtureIdAtivo={partidaAtiva?.fixture_id ?? null} />}
      {playerConfig.exibir_futebol && <TvPreGameVoting telaId={telaId ?? ''} asOverlay />}
      {playerConfig.exibir_futebol && playerConfig.exibir_bolao && <TvBolaoTicker telaId={telaId ?? ''} />}
      {playerConfig.exibir_futebol && playerConfig.exibir_bolao && (
        <TvBolaoResultadoPopup telaId={telaId ?? ''} partida={partidaAtiva} jogo={partidaJogo} />
      )}
      {playerConfig.config_batalha_vibes && <TvVibeTakeover telaId={telaId ?? ''} />}
      {playerConfig.config_invasao_junina && origemMidia === 'playlist' && (
        <TvInvasaoJuninaTakeover isAtivo={isInvasaoAtiva} />
      )}
      {playerConfig.config_tema_dia_namorados && <TvDiaNamoradosTakeover />}
      {playerConfig.config_tema_festa_junina && <TvFestaJuninaTakeover />}
      {playerConfig.config_tema_copa && (
        <TvCopaTakeover
          evento={partidaAtiva?.ultimo_evento}
          placarCasa={partidaAtiva?.placar_casa ?? 0}
          placarVisitante={partidaAtiva?.placar_visitante ?? 0}
          initialKey={lastFutebolEventKey?.startsWith('gol-') ? lastFutebolEventKey : null}
        />
      )}
      {telaId && <TvPolaroidOverlay telaId={telaId} />}
      {telaId && modoPlaylistJukebox && playerConfig.exibir_engajamento_hud && (
        <TvEngajamentoHud
          dados={engajamentoHud}
          exibirFutebol={playerConfig.exibir_futebol}
          pedidoCliente={!!pedidoAtual}
          suprimir={!!showPedidoPopup || mostrarFilaProxima || raioXVisivel}
        />
      )}
      {telaId && modoPlaylistJukebox && playerConfig.exibir_fila_proxima_tv && (
        <TvFilaProximaTicker
          itens={filaEspera}
          visivel={mostrarFilaProxima}
        />
      )}
      <QrSpotlightVignette ativo={hypeQrDestaque} />
      {showNetworkHud && <TvNetworkHud wsConnected={isOnline} />}
      <audio ref={momento.birthdayAudioRef} src={momento.audioSrc} preload="auto" />
      {/* Montado sempre, nunca condicional: criar o elemento no instante do
          disparo reinicializava a saída de áudio do Chromium e o `autoPlay`
          corria com o play() explícito do hook — era o atraso/corte no início
          da narração. `preload="auto"` mantém o pipeline pronto. */}
      <audio
        ref={identidadeSonora.locucaoAudioRef}
        src={identidadeSonora.locucaoSrc ?? undefined}
        preload="auto"
        onEnded={identidadeSonora.handleLocucaoEnded}
        onError={identidadeSonora.handleLocucaoError}
      />
    </>
  );
}
